'use client';

/**
 * AppFooter - Fully Data-Driven Application Footer
 *
 * All navigation sections come from the database via NavigationProvider,
 * grouped by navigation_groups with positions containing 'footer'.
 * No hardcoded navigation — items are controlled via the navigation_items table.
 *
 * Features:
 * - Data-driven navigation organized by DB groups
 * - Agency branding support
 * - Legal links and copyright in bottom bar
 * - Responsive grid layout
 */

import React, { useMemo } from 'react';
import Link from 'next/link';
import {
  Shield,
  Loader2,
} from 'lucide-react';
import { useAgencyBranding, useAppShell } from '../AppShellContext';
import { useNavigationOptional } from '@/lib/navigation/hooks';
import { resolveIcon } from '@/lib/navigation/icons';
import { AppVersionBadge } from '@/components/AppVersionBadge';
import type { ResolvedNavItem } from '@/lib/navigation/types';
import {
  COMPANY_NAME,
  NeutralAizAttribution,
  PrimaryBrandLogo,
  ProductCompanyLockup,
  PRODUCT_NAME,
} from '@/components/brand/EngageIQBrand';

// ============================================================================
// Types
// ============================================================================

interface AppFooterProps {
  className?: string;
}

// Simple color lookup for footer icons
const footerColors: Record<string, { iconColor: string; iconBg: string }> = {
  blue: { iconColor: 'text-blue-400', iconBg: 'bg-blue-500/10' },
  amber: { iconColor: 'text-amber-400', iconBg: 'bg-amber-500/10' },
  emerald: { iconColor: 'text-emerald-400', iconBg: 'bg-emerald-500/10' },
  cyan: { iconColor: 'text-cyan-400', iconBg: 'bg-cyan-500/10' },
  purple: { iconColor: 'text-purple-400', iconBg: 'bg-purple-500/10' },
  violet: { iconColor: 'text-violet-400', iconBg: 'bg-violet-500/10' },
  rose: { iconColor: 'text-rose-400', iconBg: 'bg-rose-500/10' },
  orange: { iconColor: 'text-orange-400', iconBg: 'bg-orange-500/10' },
  slate: { iconColor: 'text-slate-400', iconBg: 'bg-slate-500/10' },
};

function getFooterColors(colorScheme: string) {
  return footerColors[colorScheme] || footerColors.slate;
}

// ============================================================================
// Component
// ============================================================================

export function AppFooter({ className = '' }: AppFooterProps) {
  const branding = useAgencyBranding();
  const { user } = useAppShell();
  const navContext = useNavigationOptional();

  const currentYear = new Date().getFullYear();

  const customLogoUrl = branding?.logo_dark_url || branding?.logo_url;

  const companyName = branding?.company_name || COMPANY_NAME;

  // Build footer sections from DB navigation groups
  const navSections = useMemo(() => {
    const groups = navContext?.navigation?.groups ?? [];
    if (groups.length === 0) return [];

    // Filter groups that have footer position and have items with footer position
    return groups
      .filter(g => g.visible && g.positions.includes('footer'))
      .map(g => ({
        key: g.key,
        title: g.label,
        colorScheme: g.colorScheme,
        items: g.items
          .filter(i => !i.hidden && i.positions.includes('footer'))
          .sort((a, b) => a.sortOrder - b.sortOrder),
      }))
      .filter(s => s.items.length > 0)
      .sort((a, b) => {
        const groupOrder = groups.findIndex(g => g.key === a.key) - groups.findIndex(g => g.key === b.key);
        return groupOrder;
      });
  }, [navContext?.navigation?.groups]);

  const isLoading = navContext?.isLoading ?? false;

  return (
    <footer
      className={`border-t border-white/[0.06] ${className}`}
      style={{ backgroundColor: '#000' }}
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:gap-6">

          {/* Brand Column */}
          <div className="lg:w-56 flex-shrink-0">
            <Link href={user ? '/dashboard' : '/'} className="inline-block">
              <PrimaryBrandLogo
                customSrc={customLogoUrl}
                customAlt={branding?.company_name || PRODUCT_NAME}
                theme="dark"
                width={120}
                height={33}
                style={{ width: 'auto', height: 'auto', maxHeight: '26px' }}
              />
            </Link>
            {customLogoUrl ? (
              <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-white/35">
                <span>Powered by</span>
                <ProductCompanyLockup productHeight={12} companyHeight={7} />
              </div>
            ) : (
              <NeutralAizAttribution className="mt-1.5" logoHeight={9} />
            )}
            <p className="mt-1 text-xs text-white/70">
              AI-powered public safety.
            </p>
            <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-medium">
                SOC 2 • CJIS
              </span>
            </div>
          </div>

          {/* Navigation Sections — min-height reserves space for async-loaded nav to prevent CLS */}
          <div
            className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2"
            style={{ minHeight: 100 }}
          >
            {/* Data-driven sections from DB */}
            {isLoading ? (
              <div className="flex items-center gap-2 text-xs text-white/60">
                <Loader2 className="w-3 h-3 animate-spin" />
              </div>
            ) : (
              navSections.map((section) => (
                <div key={section.key} className="text-left">
                  <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-1 text-left">
                    {section.title}
                  </h3>
                  <ul className="space-y-0.5">
                    {section.items.map((item) => (
                      <FooterNavItem key={item.key} item={item} groupColor={section.colorScheme} />
                    ))}
                  </ul>
                </div>
              ))
            )}

          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <p className="text-xs text-white/60" suppressHydrationWarning>
                © {currentYear} {companyName}. All rights reserved.
              </p>
              <AppVersionBadge />
            </div>
            <div className="flex items-center gap-6">
              <a
                href="https://neutralaiz.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/60 hover:text-slate-300 transition-colors"
              >
                Privacy
              </a>
              <a
                href="https://neutralaiz.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/60 hover:text-slate-300 transition-colors"
              >
                Terms
              </a>
              <Link
                href="/accessibility"
                className="text-xs text-white/60 hover:text-slate-300 transition-colors"
              >
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// FooterNavItem Component — renders a DB navigation item in the footer
// ============================================================================

function FooterNavItem({ item, groupColor }: { item: ResolvedNavItem; groupColor: string }) {
  const colors = getFooterColors(item.colorScheme || groupColor);
  // Resolve icon from string name at render time (localStorage cache serializes icon objects)
  const Icon = resolveIcon(item.iconName);
  const linkClasses = 'group flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors';

  const content = (
    <>
      {Icon && (
        <div className={`p-1 rounded ${colors.iconBg}`}>
          <Icon className={`w-3 h-3 ${colors.iconColor}`} />
        </div>
      )}
      <span>{item.shortLabel || item.label}</span>
    </>
  );

  if (item.external) {
    return (
      <li>
        <a href={item.path} target="_blank" rel="noopener noreferrer" className={linkClasses}>
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link href={item.path} className={linkClasses}>
        {content}
      </Link>
    </li>
  );
}

// ============================================================================
// Export
// ============================================================================

export default AppFooter;
